/**
 * VidyoClient
 * Handles starting/stopping of Vidyo and sending/receiving In/Out Events.
 * Provides one interface when working with either VidyoClientForWeb (Chrome) or NPAPI plugin
 */
namespace SmartVideo {
export class VidyoClient implements IVidyoClient {
    private plugin: any;
    private proxy: ChromeProxy;
    private outEventCallbackObject: Function;
    private outEventCallbackMethods: any;
    private defaultOutEventCallbackMethod: any;
    private logCallback: any;
    private useCallbackWithPlugin: boolean;
    private config: any;
    private isChrome: boolean;
    private browser: string;
    private isSmartVideoAgent: boolean;

    constructor(isChrome: boolean, browser: string) {
        this.plugin = null;
        this.proxy = null;
        this.outEventCallbackObject = null;
        this.defaultOutEventCallbackMethod = '';
        this.logCallback = null;
        this.useCallbackWithPlugin = true;
        this.outEventCallbackMethods = {};
        this.isChrome = isChrome;
        this.browser = browser;
        this.isSmartVideoAgent = false;
        if (browser === 'SmartVideoAgent') {
            this.isSmartVideoAgent = true;
        }
    }

    setConfig(config) {
        if (config && config.plugin !== undefined) {
            this.plugin = config.plugin;
        }
        if (config && config.outEventCallbackObject !== undefined) {
            this.outEventCallbackObject = config.outEventCallbackObject;
        }
        if (config && config.defaultOutEventCallbackMethod !== undefined) {
            this.defaultOutEventCallbackMethod = config.defaultOutEventCallbackMethod;
        }
        if (config && config.logCallback !== undefined) {
            this.logCallback = config.logCallback;
        }
        if (config && config.useCallbackWithPlugin !== undefined) {
            this.useCallbackWithPlugin = config.useCallbackWithPlugin;
        }
    }

    log(message) {
        if (this.logCallback) {
            this.logCallback(message);
        }
    }

    setPlugin(plugin) {
        this.plugin = plugin;
    }

    setProxy(proxy: ChromeProxy) {
        this.proxy = proxy;
    }

    setOutEventCallbackObject(outEventCallbackObject) {
        this.outEventCallbackObject = outEventCallbackObject;
    }

    setDefaultOutEventCallbackMethod(defaultOutEventCallbackMethod) {
        this.defaultOutEventCallbackMethod = defaultOutEventCallbackMethod;
    }

    setLogCallback(logCallback) {
        this.logCallback = logCallback;
    }

    setUseCallbackWithPlugin(useCallbackWithPlugin) {
        this.useCallbackWithPlugin = useCallbackWithPlugin;
    }

    setOutEventCallbackMethod(outEventType, callbackMethod) {
        this.outEventCallbackMethods[outEventType] = callbackMethod;
    }

    start(config?) {
        let retVal = false;
        this.setConfig(config);

        if (this.isChrome || this.isSmartVideoAgent) {
            return false;
        }
        if (!this.plugin) {
            this.log('VidyoClient start invoked without plugin');
            return false;
        }
        try {
            if (this.useCallbackWithPlugin) {
                this.plugin.useCallbackForOutEvents = true;
                this.plugin.outEventCallbackObject = this;
                this.plugin.outEventCallbackMethod = 'dispatchOutEvent';
            } else {
                this.plugin.useCallbackForOutEvents = false;
                if (this.plugin.attachEvent) {
                    this.log('start() will call plugin.attachEvent()');
                    this.plugin.attachEvent('onouteventcallback', this.dispatchOutEvent.bind(this));
                } else if (this.plugin.addEventListener) {
                    this.log('start() will call plugin.addEventListener()');
                    this.plugin.addEventListener('outeventcallback', this.dispatchOutEvent.bind(this), false);
                }
            }
            retVal = this.plugin.start();
        } catch (error) {
            this.log('plugin.start() threw an exception!');
            this.log(error);
            return retVal;
        }
        if (retVal) {
            this.log('start() called plug-in with successful return.');
        } else {
            this.log('start() called plug-in with return of error!');
        }
        return retVal;
    }

    stop() {
        if (this.isChrome) {
            const request = {
                'type': 'TabUnload'
            };
            this.sendRequest(request, null);
            console.log('Tab unloaded via VidyoClient.stop()');
            return true;
        }
        let retVal = false;

        if (!this.plugin) {
            this.log('stop() invoked without plugin!');
            return false;
        }
        /* DEBUG: retVal = true;*/
        try {
            retVal = this.plugin.stop();
        } catch (error) {
            this.log('plugin.stop() threw an exception!');
            this.log(error);
            return retVal;
        }
        if (retVal) {
            console.log('stop() called plug-in with successful return.');
            this.log('stop() called plug-in with successful return.');
        } else {
            this.log('stop() called plug-in with return of error!');
        }
        return retVal;
    }

    sendEvent(event, callback) {
        let retVal = false;
        if (!this.isChrome && !this.isSmartVideoAgent) {
            if (!this.plugin) {
                this.log('sendEvent() invoked without plugin!');
                return false;
            }
        }
        if (!event || typeof event !== 'object') {
            this.log('sendEvent() invoked without valid event object!');
            return false;
        }
        if (typeof event.length === 'number' && !(event.propertyIsEnumerable('length'))) {
            this.log('sendEvent() invoked without valid event object!');
            return false;
        }
        /* DEBUG: retVal = true;*/
        try {
            if (this.isChrome || this.isSmartVideoAgent) {
                retVal = this.proxy.sendEvent(event, request => {
                    if (callback) {
                        callback(request);
                    }
                });
            } else {
                retVal = this.plugin.sendEvent(event);
                if (callback) {
                    callback(event);
                }
            }
        } catch (error) {
            this.log('plugin.sendEvent() threw an exception!');
            this.log(error);
            if (callback) {
                callback(error);
            }
        }
        return retVal;

    }

    sendRequest(request, callback) {
        let retVal = 'Error';
        if (!this.isChrome && !this.isSmartVideoAgent) {
            if (!this.plugin) {
                this.log('sendRequest() invoked without plugin!');
                return 'ErrorNoPlugin';
            }
        }
        if (!request || typeof request !== 'object') {
            this.log('sendRequest() invoked without valid request object!');
            return 'ErrorInvalidRequest';
        }
        if (typeof request.length === 'number' && !(request.propertyIsEnumerable('length'))) {
            this.log('sendRequest() invoked without valid request object!');
            return 'ErrorInvalidRequest';
        }
        /* DEBUG: retVal = true;*/
        try {
            if (this.isChrome || this.isSmartVideoAgent) {
                retVal = this.proxy.sendRequest(request, request => {
                    if (callback) {
                        callback(request);
                    }
                });
            } else {
                retVal = this.plugin.sendRequest(request);
                if (callback) {
                    callback(request);
                }
            }
        } catch (error) {
            this.log('plugin.sendRequest() threw an exception!');
            this.log(error);
            if (callback) {
                callback(error);
            }
        }
        return retVal;
    }

    isStarted() {
        if (this.isChrome || this.isSmartVideoAgent) {
            return true;
        }

        let retVal = false;
        if (!this.plugin) {
            this.log('isStarted() invoked without plugin!');
            return false;
        }
        /* DEBUG: retVal = true;*/
        try {
            retVal = this.plugin.isStarted();
        } catch (e) {
            this.log('plugin.isStarted() threw an exception!');
            this.log(e);
            return retVal;
        }
        this.log('isStarted() called plug-in.');
        return retVal;
    }

    isLoaded() {
        if (this.isChrome || this.isSmartVideoAgent) {
            return true;
        }

        if (!this.plugin) {
            this.log('isLoaded() invoked without plugin!');
            return false;
        }

        try {
            this.plugin.isStarted();
        } catch (error) {
            this.log('plugin.isLoaded() threw an exception!');
            return false;
        }

        return true;
    }

    dispatchOutEvent(event) {
        if (this.outEventCallbackObject) {
            if (event && this.outEventCallbackMethods[event.type]) {
                if (typeof this.outEventCallbackMethods[event.type] === 'function') {
                    // if method is of function type, just invoke it with default this
                    this.outEventCallbackMethods[event.type].call(this.outEventCallbackObject || this, event);
                } else {
                    // otherwise, assume method is of string type, invoke it with object as this
                    this.outEventCallbackObject[this.outEventCallbackMethods[event.type]](event);
                }
                this.log('dispatchOutEvent() invoked specific out event callback method.');
            } else if (this.defaultOutEventCallbackMethod) {
                if (typeof this.defaultOutEventCallbackMethod === 'function') {
                    // if method is of function type, just invoke it with default this
                    this.defaultOutEventCallbackMethod.call(this.outEventCallbackObject || this, event);
                } else {
                    // otherwise, assume method is of string type, invoke it with object as this
                    this.outEventCallbackObject[this.defaultOutEventCallbackMethod](event);
                }
                this.log('dispatchOutEvent() invoked default out event callback method.');
            }
        }
    }

    // Only here for inteface matching
    setSessionManager(sessionManager) {
        return;
    }
}
}
